import React from 'react';

export interface GanttTask {
  id: string;
  name: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
  status: 'pending' | 'in_progress' | 'done';
}

interface GanttChartProps {
  tasks: GanttTask[];
}

export const GanttChart: React.FC<GanttChartProps> = ({ tasks }) => {
  if (!tasks.length) return <div className="p-8 text-center text-sm text-gray-500">Sin tareas para mostrar.</div>;

  // Find overall min and max dates
  let minDate = new Date();
  let maxDate = new Date();
  let hasValidDates = false;

  tasks.forEach((t) => {
    if (t.startDate && t.endDate) {
      const s = new Date(t.startDate);
      const e = new Date(t.endDate);
      if (!hasValidDates) {
        minDate = s;
        maxDate = e;
        hasValidDates = true;
      } else {
        if (s < minDate) minDate = s;
        if (e > maxDate) maxDate = e;
      }
    }
  });

  if (!hasValidDates) {
    return <div className="p-8 text-center text-sm text-gray-500">Las tareas no tienen fechas asignadas.</div>;
  }

  // Add some padding to timeline (e.g. 5 days before, 5 days after)
  const startObj = new Date(minDate);
  startObj.setDate(startObj.getDate() - 3);
  const endObj = new Date(maxDate);
  endObj.setDate(endObj.getDate() + 5);

  const totalDays = Math.ceil((endObj.getTime() - startObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  
  if (totalDays > 365) {
     return <div className="p-8 text-center text-sm text-gray-500">El intervalo es demasiado largo para visualizarse (más de un año).</div>;
  }

  const days: { date: string; display: string }[] = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(startObj);
    d.setDate(d.getDate() + i);
    days.push({
      date: d.toISOString().split('T')[0],
      display: d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
    });
  }

  const getStatusColor = (status: string) => {
    if (status === 'done') return 'bg-emerald-500 text-emerald-950 border-emerald-400';
    if (status === 'in_progress') return 'bg-amber-500 text-amber-950 border-amber-400';
    return 'bg-gray-600 text-gray-200 border-gray-500';
  };

  return (
    <div className="overflow-x-auto w-full pb-4 scrollbar-hide">
      <div className="min-w-max">
        {/* Header row */}
        <div className="flex border-b border-white/10" style={{ width: `max(${totalDays * 40}px, 100%)` }}>
          <div className="w-48 flex-shrink-0 px-4 py-2 text-xs font-bold text-gray-400 border-r border-white/5 sticky left-0 bg-[#0d1117] z-10">
            Tarea
          </div>
          <div className="flex-1 flex">
            {days.map((d, i) => (
              <div key={i} className="w-10 flex-shrink-0 py-2 text-[10px] text-center text-gray-500 border-r border-white/5 whitespace-nowrap overflow-hidden">
                {d.display.split('/')[0]}<span className="opacity-50 text-[8px] block">{d.display.split('/')[1]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Task rows */}
        {tasks.map((task, idx) => {
          let leftOffset = 0;
          let durationDays = 0;
          let isValid = false;

          if (task.startDate && task.endDate) {
            const tStart = new Date(task.startDate);
            const tEnd = new Date(task.endDate);
            if (tStart >= startObj && tEnd <= endObj && tStart <= tEnd) {
              leftOffset = Math.floor((tStart.getTime() - startObj.getTime()) / (1000 * 60 * 60 * 24));
              durationDays = Math.ceil((tEnd.getTime() - tStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
              isValid = true;
            }
          }

          return (
            <div key={task.id || idx} className="flex border-b border-white/5 hover:bg-white/5 transition-colors group" style={{ width: `max(${totalDays * 40}px, 100%)` }}>
              <div className="w-48 flex-shrink-0 px-4 py-3 text-xs text-gray-200 border-r border-white/5 truncate sticky left-0 bg-[#0d1117] group-hover:bg-[#161b22] z-10 transition-colors">
                {task.name}
              </div>
              <div className="flex-1 relative h-10">
                {/* Background grid lines */}
                <div className="absolute inset-0 flex pointer-events-none">
                  {days.map((_, i) => (
                    <div key={i} className={`w-10 flex-shrink-0 border-r border-white/5 ${days[i].date === new Date().toISOString().split('T')[0] ? 'bg-amber-500/10' : ''}`} />
                  ))}
                </div>

                {/* Task bar */}
                {isValid && (
                  <div
                    className={`absolute top-2 h-6 rounded-md shadow-sm border text-[10px] leading-6 px-2 font-bold whitespace-nowrap overflow-hidden text-ellipsis transition-all ${getStatusColor(task.status)}`}
                    style={{
                      left: `${leftOffset * 40 + 4}px`,
                      width: `${durationDays * 40 - 8}px`
                    }}
                    title={`${task.name} (${task.startDate} - ${task.endDate})`}
                  >
                    {durationDays > 1 ? task.name : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
